import { Box, Typography, makeStyles } from "@material-ui/core";
import React from "react";
import { useHistory } from "react-router";

import { CustomButton } from "@/components/Button";
import { buildDiagnosisPhaseTags } from "@/components/diagnosis/DiagnosisPhaseTags";
import { useFromHomeEntry } from "@/hooks/useFromHomeEntry";
import { useInvestmentStyleFunnelEvent } from "@/hooks/useInvestmentStyleFunnelEvent";
import { DiagnosisLayout, diagnosisActionStyles } from "@/layouts/DiagnosisLayout";
import theme from "@/theme";
import { AppEventTokenEnum } from "@/utilities/capacitorLogic";

import ResultSampleImage from "@assets/image/investment_style_diagnosis_intro_result_sample.png";

const useStyles = makeStyles(() => ({
  "@keyframes introEnter": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  "@keyframes sampleEnter": {
    from: { opacity: 0, transform: "translateY(20px) scale(0.985)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  reveal: {
    animation: "$introEnter 480ms cubic-bezier(0.22, 1, 0.36, 1) both",
    "@media (prefers-reduced-motion: reduce)": { "&&": { animation: "none" } },
  },
  descriptionReveal: { animationDelay: "100ms" },
  sampleReveal: {
    animationName: "$sampleEnter",
    animationDuration: "620ms",
    animationDelay: "200ms",
  },
  noteReveal: { animationDelay: "360ms" },
  actionsReveal: {
    animationDelay: "480ms",
    "&:focus-within": { animation: "none" },
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.3,
    textAlign: "center",
    color: theme.palette.primary.main,
    [theme.breakpoints.up("md")]: { fontSize: 24 },
  },
  headingAccent: {
    color: theme.palette.primary.main,
  },
  description: {
    marginTop: 24,
    fontSize: 14,
    lineHeight: 1.5,

    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  sampleWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 8,
    margin: "24px 0",
  },
  sampleLabel: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5,

    color: theme.palette.semantic["text/heading"],
    [theme.breakpoints.up("md")]: { fontSize: 14 },
  },
  // 診断結果画面の見本画像を上端付近だけ見せる窓（Figma の CROP と同じ切り取り）。
  // 画像は幅いっぱい・縦横比のままにし、画像の高さの 5.3935% ぶん上へずらして表紙の見出しから見せる。
  // 窓は SP の Figma（342×240）と同じ縦横比で幅に合わせて伸ばし、PC でも SP と同じ範囲（カードの2行目まで）を見せる
  // （Figma の PC は画像を横に引き伸ばして 464×240 に収めているが、文字が歪むので真似しない）
  sampleFrame: {
    position: "relative",
    width: "100%",
    paddingTop: `${(240 / 342) * 100}%`,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: theme.palette.semantic["surface/base"],
  },
  sampleImage: {
    position: "absolute",
    top: 0,
    left: 0,
    display: "block",
    width: "100%",
    height: "auto",
    maxWidth: "none",
    transform: "translateY(-5.3935%)",
  },
  note: {
    marginBottom: 0,
    fontSize: 14,
    lineHeight: 1.5,

    color: theme.palette.text.secondary,
    [theme.breakpoints.up("md")]: { fontSize: 16 },
  },
  actions: diagnosisActionStyles,
}));

export const DiagnosisInvestmentStyleIntroPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const fromHome = useFromHomeEntry();

  useInvestmentStyleFunnelEvent(AppEventTokenEnum.InvestmentStyleDiagnosisIntroAccess);

  return (
    <DiagnosisLayout phaseTags={buildDiagnosisPhaseTags(fromHome, false)}>
      <Typography className={`${classes.heading} ${classes.reveal}`}>
        あなた専用の「<span className={classes.headingAccent}>投資判断基準</span>」を
        <br />
        作成します。
      </Typography>

      <Typography className={`${classes.description} ${classes.reveal} ${classes.descriptionReveal}`}>
        この診断は、あなたの目的・リスク許容度・優先条件を分析し、<b>狙うべき物件・避けるべき物件</b>
        を明らかにするものです。
      </Typography>

      <Box className={`${classes.sampleWrap} ${classes.reveal} ${classes.sampleReveal}`}>
        <Typography className={classes.sampleLabel}>診断イメージ</Typography>
        <Box className={classes.sampleFrame}>
          <img
            src={ResultSampleImage}
            alt="診断結果の見本（投資スタイル「長期保有型」と、目的と理想のカード）"
            className={classes.sampleImage}
          />
        </Box>
      </Box>

      <Typography className={`${classes.note} ${classes.reveal} ${classes.noteReveal}`}>
        プロパリーでは、あなたの理想・考え方・スタンスを知ることで、あなたの「はじめての不動産投資」を、納得できる一歩に変えていきます。
      </Typography>

      {/* 入口の state をフォームの履歴エントリへ引き継ぐ（ホーム起点のチップス非表示・出口切り替えを維持するため） */}
      <Box className={`${classes.actions} ${classes.reveal} ${classes.actionsReveal}`}>
        <CustomButton
          type="button"
          tone="primary"
          buttonSize="L"
          fullWidth
          onClick={() => history.push("/investment_style_diagnosis", { fromHome })}
        >
          自分に合う進め方を診断する
        </CustomButton>

        {/* このテンプレートには戻る手段が無く、iOS ネイティブはハードウェアバックも無いため、
          ホーム起点で任意に開いた場合のみテキストボタンで戻れるようにする
          （登録導線は診断完了が必須のため出さない） */}
        {fromHome && (
          <Box mt={0.5}>
            <CustomButton type="button" tone="text" buttonSize="L" fullWidth onClick={() => history.goBack()}>
              戻る
            </CustomButton>
          </Box>
        )}
      </Box>
    </DiagnosisLayout>
  );
};
